import * as Sentry from "@sentry/react";
import jwt_decode from "jwt-decode";
import {
  clearSessionToken,
  forgetSessionToken,
  getSessionToken,
  peekSessionToken,
  pinSessionToken,
  setSessionTokenLostHandler,
  unpinSessionToken,
} from "./sessionToken";

export const SESSION_EXPIRED_EVENT = "knowtex:session-expired";
export const SESSION_EXPIRED_PATH = "/session-expired";

/**
 * The longest a recording can run (2 hours) plus 15 minutes for pauses and the upload,
 * conversation request and status changes at the end, so the session outlasts all of it.
 */
export const RECORDING_MIN_SESSION_MS = (2 * 60 + 15) * 60 * 1000;

// server/middleware/authorization.js sends exactly this body for a missing, invalid or
// expired JWT, and server/middleware/authorization.test.js pins it so this stays true.
// Feature-gate 403s send an { error } object, so they never match.
const SESSION_REJECTED_BODY = "Not Authorized";

// Ending the session unmounts the recorder, which stops capture, so between a recording
// starting and its upload finishing the end is held back until the recording is done.
// Counted, not a flag: a new recording can start while the previous one's upload is
// still finishing, and neither may release the other's hold.
let openRecordingHolds = 0;
let sessionEndDeferred = false;
let sessionEndCount = 0;

const readSentToken = (init?: RequestInit): string | null => {
  const headers = init?.headers;
  if (!headers) return null;

  let value: string | null | undefined;
  if (typeof (headers as Headers).get === "function") {
    value = (headers as Headers).get("token");
  } else if (Array.isArray(headers)) {
    value = headers.find(([name]) => name.toLowerCase() === "token")?.[1];
  } else {
    value = (headers as Record<string, string | null | undefined>).token;
  }

  // Some callers pass a missing token straight into the header, which sends "null".
  return value && value !== "null" && value !== "undefined" ? value : null;
};

function endSession(): void {
  if (openRecordingHolds > 0) {
    if (!sessionEndDeferred) {
      try {
        Sentry.logger.info("Session end deferred until the recording finishes", {
          event: "session.end_deferred_recording",
        });
      } catch {
        // Logging must never change whether the session ends.
      }
    }
    sessionEndDeferred = true;
    return;
  }
  sessionEndCount += 1;
  window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
}

/**
 * Call when a recording has started. Until the returned release runs (after the upload
 * finishes, or the recording is discarded), session ends are held and the token current
 * at the start is kept for the recording's requests. Releasing more than once is a no-op.
 * When the last open hold is released, a held session end fires unless a fresh token was
 * stored in the meantime.
 */
export function holdSessionForRecording(): () => void {
  if (openRecordingHolds === 0) pinSessionToken();
  openRecordingHolds += 1;

  let released = false;
  return () => {
    if (released) return;
    released = true;
    openRecordingHolds -= 1;
    if (openRecordingHolds > 0) return;

    unpinSessionToken();
    if (!sessionEndDeferred) return;
    sessionEndDeferred = false;
    if (!peekSessionToken()) endSession();
  };
}

export function isRecordingHeld(): boolean {
  return openRecordingHolds > 0;
}

// Set when a session is ended during page load, before the app has subscribed to
// session ends, so the app still shows the session-expired page instead of treating
// the user as never signed in. Per tab, like the load it belongs to.
const EXPIRED_ON_LOAD_KEY = "sessionExpiredOnLoad";

export function markSessionExpiredOnLoad(): void {
  try {
    sessionStorage.setItem(EXPIRED_ON_LOAD_KEY, "1");
  } catch {
    // Blocked storage: the user lands on the sign-in page instead.
  }
}

/** Whether a session was ended during this page load. Does not clear the flag. */
export function sessionExpiredOnLoad(): boolean {
  try {
    return sessionStorage.getItem(EXPIRED_ON_LOAD_KEY) === "1";
  } catch {
    return false;
  }
}

export function clearSessionExpiredOnLoad(): void {
  try {
    sessionStorage.removeItem(EXPIRED_ON_LOAD_KEY);
  } catch {
    // Blocked storage has nothing to clear.
  }
}

/** Test-only. */
export function resetRecordingState(): void {
  openRecordingHolds = 0;
  sessionEndDeferred = false;
}

/**
 * Drops the rejected token and tells the app the session is over. A null token means the
 * request went out without one. Another launch in this browser may have stored a fresh
 * token while the request was in flight; in that case the next request uses it and the
 * session stays.
 */
export function handleSessionRejection(rejectedToken: string | null): void {
  if (rejectedToken) forgetSessionToken(rejectedToken);
  if (peekSessionToken()) return;
  endSession();
}

/**
 * Signs out after inactivity. Does nothing while a recording is open, so the caller tries
 * again later and the recording's token stays valid until its upload finishes.
 */
export function endIdleSession(): void {
  if (openRecordingHolds > 0) return;
  clearSessionToken();
  endSession();
}

// Not handleSessionRejection: mid-recording the pinned token would make the session look alive.
setSessionTokenLostHandler(endSession);

const readClaims = (token: string): { exp?: number; user?: { id?: string } } | null => {
  try {
    return jwt_decode(token);
  } catch {
    return null;
  }
};

/**
 * Ends the session now if its token expires within `minRemainingMs`, so work that can't
 * survive a logout part-way through never starts. Returns whether there is enough time
 * left. A token without a readable expiry passes: the server still rejects it if invalid.
 * So does a page that never had a token. A token that has gone missing from storage
 * ends the session and fails, and so does a missing token while a session end is held.
 * `action` names the work being guarded in the Sentry log.
 */
export function ensureSessionTimeLeft(minRemainingMs: number, action: string): boolean {
  const endsBefore = sessionEndCount;
  const token = getSessionToken();
  // No token is fine only if the session isn't ending: reading it can find it gone from
  // storage and end the session right here, or an earlier rejection may have dropped it
  // with the end still held by another recording's upload.
  if (!token) return sessionEndCount === endsBefore && !sessionEndDeferred;

  const claims = readClaims(token);
  if (typeof claims?.exp !== "number") return true;
  const remainingMs = claims.exp * 1000 - Date.now();
  if (remainingMs >= minRemainingMs) return true;

  try {
    Sentry.logger.info("Session ended before starting work with too little time left", {
      event: "session.ended_low_time_left",
      action,
      minutes_left: Math.round(remainingMs / 60000),
      required_minutes: Math.round(minRemainingMs / 60000),
      ...(claims.user?.id ? { user_id: claims.user.id } : {}),
    });
  } catch {
    // Logging must never block ending the session.
  }

  handleSessionRejection(token);
  const remaining = peekSessionToken();
  if (!remaining) return false;
  // Another launch stored a different token; judge that one instead.
  if (remaining !== token) return ensureSessionTimeLeft(minRemainingMs, action);
  // The token couldn't be cleared from storage, so end the session without it.
  endSession();
  return false;
}

const checkForRejection = async (response: Response, sentToken: string | null): Promise<void> => {
  const body = await response.json().catch(() => null);
  if (body === SESSION_REJECTED_BODY) {
    handleSessionRejection(sentToken);
  }
};

/**
 * Wraps fetch so every authenticated request, including raw fetch calls and streams,
 * ends the session when the server rejects its JWT.
 */
export function withSessionExpiry(baseFetch: typeof fetch): typeof fetch {
  // Until a request carries a token there is no session to end: a first visit's
  // /auth/is-verify, or a launch that hasn't stored its token yet, is rejected too.
  let hasSentToken = false;

  return async (input, init) => {
    const sentToken = readSentToken(init);
    if (sentToken) hasSentToken = true;

    const response = await baseFetch(input, init);
    if (response.status === 403 && hasSentToken) {
      await checkForRejection(response.clone(), sentToken);
    }
    return response;
  };
}

let isInstalled = false;

export function installSessionExpiryWatcher(): void {
  if (isInstalled) return;
  isInstalled = true;
  window.fetch = withSessionExpiry(window.fetch.bind(window));
}

/** Calls onExpired each time a request's JWT is rejected. Returns an unsubscribe. */
export function onSessionExpired(onExpired: () => void): () => void {
  window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
  return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
}
